import { useState, useEffect, useCallback } from "react";
import { trackReminderToggled } from "../services/analytics";

const STORAGE_KEY = "reminder_settings";
const REMINDER_TIMER_KEY = "reminder_timer_id";

function getSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { enabled: false, hour: 20, minute: 0 };
}

function saveSettings(s) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export default function ReminderSettings({ streak }) {
  const [settings, setSettings] = useState(getSettings);
  const [permState, setPermState] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );

  const scheduleReminder = useCallback((hour, minute) => {
    // Clear existing timer
    const existingId = localStorage.getItem(REMINDER_TIMER_KEY);
    if (existingId) clearTimeout(Number(existingId));

    const now = new Date();
    const target = new Date(now);
    target.setHours(hour, minute, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);

    const delay = target - now;
    const timerId = setTimeout(() => {
      if (Notification.permission === "granted") {
        new Notification("1% Better Everyday 🔥", {
          body: streak?.current_streak > 0
            ? `Đừng quên giữ streak ${streak.current_streak} ngày! Hoàn thành thử thách hôm nay nhé.`
            : "Hôm nay bạn đã nhận thử thách chưa? Chỉ 2 phút thôi!",
          icon: "/logo192.png",
          badge: "/logo192.png",
          tag: "daily-reminder",
        });
      }
      // Schedule next day
      scheduleReminder(hour, minute);
    }, delay);

    localStorage.setItem(REMINDER_TIMER_KEY, String(timerId));
  }, [streak]);

  useEffect(() => {
    if (settings.enabled && permState === "granted") {
      scheduleReminder(settings.hour, settings.minute);
    }
    return () => {
      const id = localStorage.getItem(REMINDER_TIMER_KEY);
      if (id) clearTimeout(Number(id));
    };
  }, [settings.enabled, settings.hour, settings.minute, permState, scheduleReminder]);

  const handleToggle = async () => {
    if (!settings.enabled) {
      // Turning on — request permission
      if (typeof Notification === "undefined") return;
      if (Notification.permission === "default") {
        const perm = await Notification.requestPermission();
        setPermState(perm);
        if (perm !== "granted") return;
      } else if (Notification.permission === "denied") {
        return; // Can't request again
      }
      const newSettings = { ...settings, enabled: true };
      setSettings(newSettings);
      saveSettings(newSettings);
      trackReminderToggled(true);
    } else {
      // Turning off
      const id = localStorage.getItem(REMINDER_TIMER_KEY);
      if (id) clearTimeout(Number(id));
      localStorage.removeItem(REMINDER_TIMER_KEY);
      const newSettings = { ...settings, enabled: false };
      setSettings(newSettings);
      saveSettings(newSettings);
      trackReminderToggled(false);
    }
  };

  const handleTimeChange = (e) => {
    const [h, m] = e.target.value.split(":").map(Number);
    const newSettings = { ...settings, hour: h, minute: m };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const timeStr = `${String(settings.hour).padStart(2, "0")}:${String(settings.minute).padStart(2, "0")}`;

  return (
    <div className="reminder-settings">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" }}>
          🔔 Nhắc nhở hàng ngày
        </div>
      </div>

      {/* Toggle */}
      <div className="reminder-toggle" style={{ marginBottom: settings.enabled ? "16px" : 0 }}>
        <div>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
            {settings.enabled ? "Đang bật nhắc nhở" : "Bật nhắc nhở để giữ streak"}
          </div>
          {permState === "denied" && (
            <div style={{ fontSize: "11px", color: "#EF4444", marginTop: "4px" }}>
              Trình duyệt đã chặn thông báo. Vui lòng bật trong cài đặt.
            </div>
          )}
        </div>
        <label className="toggle-switch">
          <input type="checkbox" checked={settings.enabled} onChange={handleToggle} disabled={permState === "denied"} />
          <span className="toggle-slider" />
        </label>
      </div>

      {/* Time picker */}
      {settings.enabled && (
        <div style={{
          display: "flex", alignItems: "center", gap: "12px",
          background: "var(--bg-inset)", borderRadius: "10px", padding: "12px",
        }}>
          <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>Nhắc lúc:</span>
          <input type="time" value={timeStr} onChange={handleTimeChange}
            style={{
              background: "var(--bg-card)", border: "1px solid var(--border-primary)",
              borderRadius: "8px", padding: "6px 10px", color: "var(--text-primary)",
              fontSize: "14px", fontWeight: "600",
            }} />
          <span style={{ fontSize: "11px", color: "var(--text-dim)", marginLeft: "auto" }}>
            mỗi ngày
          </span>
        </div>
      )}
    </div>
  );
}
